import React, { useState, useEffect } from 'react';

// Helper function to get the API base URL
const getApiBaseUrl = () => {
  // Always use relative URLs to work with proxy
  return '/api';
};

interface TierListShareProps {
  tiers: any[];
}

export default function TierListShare({ tiers }: TierListShareProps) {
  const [copied, setCopied] = useState(false);
  const [showShareLink, setShowShareLink] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Effect to close the share section if tiers change after link generation
  useEffect(() => {
    // Only run if the share link section is currently visible
    if (showShareLink) {
      // console.log('[TierListShare] Tiers changed while share link was visible. Closing section.');
      // Reset the state to hide the share link section
      setShowShareLink(false);
      setShareUrl(''); // Clear the old URL
      setError('');    // Clear any previous error
      setCopied(false);  // Reset copied state
    }
    // We want this effect to run *every time* tiers change,
    // specifically to detect changes *after* the link was generated and shown.
  }, [tiers]); // Dependency array includes only tiers

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleGenerateLink = async () => {
    setIsLoading(true);
    setError('');
    setShowShareLink(true);

    try {
      const response = await fetch(`${getApiBaseUrl()}/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tiers: tiers }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.id) {
        const generatedUrl = `${window.location.origin}/view/${data.id}`;
        setShareUrl(generatedUrl);
      } else {
        throw new Error('Share ID not received from server.');
      }
    } catch (e) {
      console.error("Failed to generate share link:", e);
      const errorMessage = e instanceof Error ? e.message : 'Failed to generate link. Please try again.';
      setError(errorMessage);
      setShowShareLink(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-6 bg-gray-800 p-4 rounded-lg shadow-sm">
      <h3 className="font-bold mb-3 text-white">Share your tier list</h3>
      
      {!showShareLink ? (
        <button 
          onClick={handleGenerateLink}
          className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 text-sm disabled:opacity-50"
          disabled={isLoading}
        >
          {isLoading ? 'Generating...' : 'Generate Share Link'}
        </button>
      ) : (
        <>
          <div className="flex justify-between items-center mb-3">
            <div className="text-xs text-gray-400">Anyone with the link can view your tier list.</div>
            <button 
              onClick={handleCopyUrl}
              className={`text-sm px-3 py-1 rounded transition-colors ${
                copied 
                  ? 'bg-green-600 text-white' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <input
              className="w-full border border-gray-600 rounded px-2 py-2 text-sm bg-gray-700 text-white placeholder-gray-400"
              value={shareUrl}
              readOnly
              onFocus={e => e.target.select()}
            />
          </div>
          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        </>
      )}
    </div>
  );
}

export {} 